import React, {type ReactElement} from "react";
import {Helmet} from "react-helmet";

import {css} from "/src/utils/utils";

import ModuleBody from "../../layouts/module-body/ModuleBody";
import * as styles from "./wistia-channel.module.scss";

interface WistiaChannelProps {
  copy: string;
  darkMode?: boolean;
  headline: string;
  id: string;
}

const WistiaChannel = (props: WistiaChannelProps): ReactElement | null => {
  const {copy, darkMode = false, headline, id} = props;

  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      setMounted(true);
    }
  }, []);

  const wcq = () => {
    /* eslint-disable no-underscore-dangle */
    // @ts-ignore
    window._wcq = window._wcq || [];

    // @ts-ignore
    window._wcq.push({
      id: "_all",
      options: {
        customFont: "GT-America-Standard-Regular",
        // disable routing because it somehow breaks the page
        history: {
          back: () => {},
          pushState: () => {},
          replaceState: () => {},
        },
      },
    });
    /* eslint-enable no-underscore-dangle */
  };

  const wistiaClasses = [
    "wistia_channel",
    `wistia_async_${id}`,
    "mode=inline",
    "videoCardsLayout=grid",
    "headerFontSizeMultiplier=.95",
  ].join(" ");

  if (!mounted) {
    return null;
  }

  // FMH-671: Temp workaround to address
  // ketch suppression implementation
  const consent = window.ketchConsent ?? {};
  const scriptAttributes = {
    type: consent?.analytics === true ? "text/javascript" : "text/plain",
    "data-purposes": "analytics",
  };

  return (
    <section
      className={css(
        styles.wistiaChannel,
        darkMode ? styles.wistiaChannelDark : ""
      )}
    >
      <ModuleBody>
        <Helmet>
          <link
            rel="stylesheet"
            href={`https://fast.wistia.com/embed/channel/project/${id}/font.css`}
          />
          <script
            src="https://fast.wistia.com/assets/external/channel.js"
            async
            {...scriptAttributes}
          ></script>
          <script {...scriptAttributes} nonce="{{nonce}}">
            {/* @ts-ignore */}
            {wcq()}
          </script>
        </Helmet>

        <div className={css(styles.wistiaChannel__module)}>
          {(headline != null || copy != null) && (
            <div className={css(styles.wistiaChannel__text)}>
              {headline != null && (
                <h2 className={css("h2", styles.wistiaChannel__h2)}>
                  {headline}
                </h2>
              )}

              {copy != null && (
                <div className={css(styles.wistiaChannel__copy)}>
                  <p className={css("p", styles.wistiaChannel__p)}>{copy}</p>
                </div>
              )}
            </div>
          )}

          <div className={wistiaClasses} style={{position: "relative"}}></div>
        </div>
      </ModuleBody>
    </section>
  );
};

export default WistiaChannel;
